import {
  Button,
  Card,
  CardContent,
  Checkbox,
  Divider,
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
  FormControlLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  CircularProgress,
  Tooltip,
  Box,
  Avatar,
  Chip,
  Paper,
  Container,
  Alert,
  Fade
} from "@mui/material";
import ExpandMore from "@mui/icons-material/ExpandMore";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import SportsEsportsIcon from "@mui/icons-material/SportsEsports";
import SettingsIcon from "@mui/icons-material/Settings";
import { useFormik, Form, FormikProvider, FieldArray } from "formik";
import { boostersServices } from "../../services/AllServices";
import { useNavigate, useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import errorHandler from "../../utils/errorHandler";
import * as Yup from "yup";
import Iconify from "../../components/iconify";

// Constants for select options
const TYPE_OPTIONS = [
  { value: "power-captain", label: "Power Captain", icon: "mdi:crown" },
  // Add more types if needed
];
const DURATION_OPTIONS = [
  { value: "one_gameweek", label: "1 Gameweek" },
  { value: "two_gameweek", label: "2 Gameweeks" },
  // Add more options if needed
];
const CONTEST_TYPES = [
  { value: "daily", label: "Daily", icon: "mdi:calendar-day" },
  { value: "week", label: "Weekly", icon: "mdi:calendar-week" },
  { value: "monthly", label: "Monthly", icon: "mdi:calendar-month" },
  { value: "season", label: "Season", icon: "mdi:calendar" }
];

const validationSchema = Yup.object().shape({
  name: Yup.string().required("Name is required"),
  description: Yup.string().required("Description is required"),
  type: Yup.string().required("Type is required"),
  allowedGameTypes: Yup.array().of(
    Yup.object().shape({
      gameType: Yup.string().required("Game type is required"),
      allowedContestTypes: Yup.array()
        .of(Yup.string())
        // .min(1, "Select at least one contest type"),
    })
  ),
});

export default function AddEditBoostersManagement() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = useState(false);
  const [expandedPanel, setExpandedPanel] = useState(0); // Default to first game type expanded

  const formik = useFormik({
    initialValues: {
      name: "",
      description: "",
      type: "",
      allowedGameTypes: [],
    },
    validationSchema,
    onSubmit: (values, { setSubmitting }) => {
      const apiCall = id
        ? boostersServices.updateBoosters({ id, ...values })
        : boostersServices.addBoosters(values);

      apiCall
        .then((res) => {
          toast.success(res?.data?.message);
          navigate("/boosters-management");
          setSubmitting(false);
        })
        .catch((err) => {
          errorHandler(err);
          setSubmitting(false);
        });
    },
  });

  const { errors, touched, isSubmitting, handleSubmit, getFieldProps, values, setValues } =
    formik;

  useEffect(() => {
    if (id) {
      setLoading(true);
      boostersServices
        .getOneBoosters({ id })
        .then((res) => {
          const data = res?.data?.data;
          setValues({
            name: data.name,
            description: data.description,
            type: data.type,
            allowedGameTypes: data.allowedGameTypes,
          });
        })
        .catch((err) => errorHandler(err))
        .finally(() => setLoading(false));
    }
  }, [id, setValues]);

  const handleAccordionChange = (panel) => (event, isExpanded) => {
    setExpandedPanel(isExpanded ? panel : false);
  };

  if (loading) return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Stack alignItems="center" justifyContent="center" sx={{ mt: 8, minHeight: '400px' }}>
        <CircularProgress size={60} sx={{ color: 'primary.main' }} />
        <Typography sx={{ mt: 3, color: 'text.secondary' }} variant="h6">
          Loading booster details...
        </Typography>
    </Stack>
    </Container>
  );

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Fade in timeout={500}>
        <Stack spacing={4}>
          {/* Header Section */}
          <Paper
            elevation={0}
            sx={{
              p: 4,
              borderRadius: 3,
              background: 'linear-gradient(135deg, #1a202c 0%, #2d3748 100%)',
              border: '1px solid',
              borderColor: 'divider',
              position: 'relative',
              overflow: 'hidden',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: '4px',
                background: 'linear-gradient(90deg, #00a1dd 0%, #42a5f5 50%, #90caf9 100%)',
              }
            }}
          >
            <Stack direction="row" alignItems="center" spacing={3}>
              <Avatar
                sx={{
                  width: 64,
                  height: 64,
                  background: 'linear-gradient(135deg, #00a1dd 0%, #42a5f5 100%)',
                  boxShadow: '0 8px 32px rgba(0, 161, 221, 0.4)'
                }}
              >
                <AutoAwesomeIcon sx={{ fontSize: 32, color: 'white' }} />
              </Avatar>
              <Box>
                <Typography variant="h4" sx={{ color: 'white', fontWeight: 700, mb: 1 }}>
                  {id ? "Edit" : "Create"} Booster
                </Typography>
                <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                  {id ? "Update booster configuration and settings" : "Configure new booster with game types and contest types"}
                </Typography>
              </Box>
            </Stack>
          </Paper>

        <FormikProvider value={formik}>
          <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
              <Stack spacing={4}>
                {/* Booster Details Section */}
                <Card
                  elevation={0}
                  sx={{
                    borderRadius: 3,
                    border: '1px solid',
                    borderColor: 'divider',
                    background: 'linear-gradient(145deg, #1a202c 0%, #2d3748 100%)',
                    overflow: 'hidden'
                  }}
                >
                  <Box
                    sx={{
                      p: 3,
                      background: 'linear-gradient(90deg, rgba(0, 161, 221, 0.1) 0%, rgba(66, 165, 245, 0.1) 100%)',
                      borderBottom: '1px solid',
                      borderColor: 'divider'
                    }}
                  >
                    <Stack direction="row" alignItems="center" spacing={2}>
                      <Avatar
                        sx={{
                          width: 40,
                          height: 40,
                          background: 'linear-gradient(135deg, #00a1dd 0%, #42a5f5 100%)'
                        }}
                      >
                        <SettingsIcon sx={{ fontSize: 20, color: 'white' }} />
                      </Avatar>
                      <Typography variant="h6" sx={{ color: 'white', fontWeight: 600 }}>
                        Booster Details
            </Typography>
                    </Stack>
                  </Box>

                  <CardContent sx={{ p: 4 }}>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                          label="Booster Name"
                  {...getFieldProps("name")}
                  error={Boolean(touched.name && errors.name)}
                  helperText={touched.name && errors.name}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              borderRadius: 2,
                              '&:hover .MuiOutlinedInput-notchedOutline': {
                                borderColor: 'primary.main',
                              },
                            },
                          }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                          label="Booster Type"
                  value={
                    TYPE_OPTIONS.find(option => option.value === values.type)?.label || ""
                  }
                  disabled
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              borderRadius: 2,
                              backgroundColor: 'rgba(255, 255, 255, 0.05)',
                            },
                          }}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Description"
                  {...getFieldProps("description")}
                  error={Boolean(touched.description && errors.description)}
                  helperText={touched.description && errors.description}
                  multiline
                          minRows={3}
                          maxRows={6}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              borderRadius: 2,
                              '&:hover .MuiOutlinedInput-notchedOutline': {
                                borderColor: 'primary.main',
                              },
                            },
                          }}
                />
              </Grid>
            </Grid>
                  </CardContent>
                </Card>

            {/* Allowed Game Types Section */}
                <Card
                  elevation={0}
                  sx={{
                    borderRadius: 3,
                    border: '1px solid',
                    borderColor: 'divider',
                    background: 'linear-gradient(145deg, #1a202c 0%, #2d3748 100%)',
                    overflow: 'hidden'
                  }}
                >
                  <Box
                    sx={{
                      p: 3,
                      background: 'linear-gradient(90deg, rgba(156, 39, 176, 0.1) 0%, rgba(233, 30, 99, 0.1) 100%)',
                      borderBottom: '1px solid',
                      borderColor: 'divider'
                    }}
                  >
                    <Stack direction="row" alignItems="center" justifyContent="space-between">
                      <Stack direction="row" alignItems="center" spacing={2}>
                        <Avatar
                          sx={{
                            width: 40,
                            height: 40,
                            background: 'linear-gradient(135deg, #9c27b0 0%, #e91e63 100%)'
                          }}
                        >
                          <SportsEsportsIcon sx={{ fontSize: 20, color: 'white' }} />
                        </Avatar>
                        <Box>
                          <Typography variant="h6" sx={{ color: 'white', fontWeight: 600 }}>
                            Game Types Configuration
                          </Typography>
                          <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                            Configure which contest types are allowed for each game type
                          </Typography>
                        </Box>
                      </Stack>
                      <Chip
                        label={`${values.allowedGameTypes?.length || 0} Game Types`}
                        sx={{
                          backgroundColor: 'rgba(156, 39, 176, 0.2)',
                          color: '#e91e63',
                          fontWeight: 600,
                          border: '1px solid rgba(156, 39, 176, 0.3)'
                        }}
                      />
                    </Stack>
                  </Box>
                  
                  <CardContent sx={{ p: 4 }}>
            <FieldArray
              name="allowedGameTypes"
              render={() => (
                <>
                  {values.allowedGameTypes && values.allowedGameTypes.length > 0 ? (
                            <Stack spacing={3}>
                              {values.allowedGameTypes.map((game, idx) => (
                                <Accordion
                                  key={idx}
                                  expanded={expandedPanel === idx}
                                  onChange={handleAccordionChange(idx)}
                                  sx={{
                                    borderRadius: 3,
                                    background: 'linear-gradient(145deg, rgba(255, 255, 255, 0.03) 0%, rgba(255, 255, 255, 0.05) 100%)',
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    overflow: 'hidden',
                                    transition: 'all 0.3s ease',
                                    '&:before': { display: 'none' },
                                    '&:hover': {
                                      borderColor: 'primary.main',
                                      transform: 'translateY(-2px)',
                                      boxShadow: '0 8px 32px rgba(0, 161, 221, 0.15)',
                                    },
                                    '&.Mui-expanded': {
                                      borderColor: 'primary.main',
                                      boxShadow: '0 8px 32px rgba(0, 161, 221, 0.15)',
                                    }
                                  }}
                                >
                                  {/* Game Type Header */}
                                  <AccordionSummary
                                    expandIcon={<ExpandMore sx={{ color: 'primary.main' }} />}
                                    sx={{
                                      p: 3,
                                      background: 'linear-gradient(90deg, rgba(0, 161, 221, 0.1) 0%, rgba(66, 165, 245, 0.1) 100%)',
                                      borderBottom: '1px solid',
                                      borderColor: 'divider',
                                      '& .MuiAccordionSummary-content': {
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        m: 0
                                      }
                                    }}
                                  >
                                    <Stack direction="row" alignItems="center" spacing={2}>
                                      <Avatar
                                        sx={{
                                          width: 48,
                                          height: 48,
                                          background: 'linear-gradient(135deg, #00a1dd 0%, #42a5f5 100%)',
                                          boxShadow: '0 4px 16px rgba(0, 161, 221, 0.3)'
                                        }}
                                      >
                                        <Iconify icon="mdi:gamepad-variant" width={24} />
                                      </Avatar>
                                      <Box>
                                        <Typography variant="h6" sx={{ color: 'white', fontWeight: 700 }}>
                            {game.gameType?.toUpperCase()}
                          </Typography>
                                        <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                                          Game Type Configuration
                                        </Typography>
                                      </Box>
                                    </Stack>
                                    <Chip
                                      label={`${game.allowedContestTypes?.length || 0} Selected`}
                                      size="small"
                                      sx={{
                                        backgroundColor: 'rgba(0, 161, 221, 0.2)',
                                        color: 'primary.main',
                                        fontWeight: 600,
                                        border: '1px solid rgba(0, 161, 221, 0.3)'
                                      }}
                                    />
                        </AccordionSummary>

                                  {/* Contest Types Configuration */}
                                  <AccordionDetails sx={{ p: 3 }}>
                                    <Typography variant="subtitle2" sx={{ color: 'white', mb: 3, fontWeight: 600 }}>
                                      Select Contest Types for {game.gameType?.toUpperCase()}:
                                    </Typography>
                                    
                          <Grid container spacing={2}>
                                      {CONTEST_TYPES.map((option) => {
                                        const isSelected = game.allowedContestTypes.includes(option.value);
                                        return (
                                          <Grid item xs={12} sm={6} md={3} key={option.value}>
                                            <Paper
                                              elevation={0}
                                              sx={{
                                                p: 2,
                                                borderRadius: 2,
                                                cursor: 'pointer',
                                                border: '2px solid',
                                                borderColor: isSelected ? 'primary.main' : 'divider',
                                                background: isSelected 
                                                  ? 'linear-gradient(135deg, rgba(0, 161, 221, 0.1) 0%, rgba(66, 165, 245, 0.1) 100%)'
                                                  : 'rgba(255, 255, 255, 0.02)',
                                                transition: 'all 0.3s ease',
                                                '&:hover': {
                                                  borderColor: isSelected ? 'primary.main' : 'primary.light',
                                                  background: isSelected 
                                                    ? 'linear-gradient(135deg, rgba(0, 161, 221, 0.15) 0%, rgba(66, 165, 245, 0.15) 100%)'
                                                    : 'rgba(255, 255, 255, 0.05)',
                                                  transform: 'translateY(-2px)',
                                                }
                                              }}
                                              onClick={() => {
                                          let newContestTypes = [...values.allowedGameTypes[idx].allowedContestTypes];
                                                if (isSelected) {
                                                  newContestTypes = newContestTypes.filter((c) => c !== option.value);
                                          } else {
                                                  newContestTypes.push(option.value);
                                          }
                                          formik.setFieldValue(
                                            `allowedGameTypes[${idx}].allowedContestTypes`,
                                            newContestTypes
                                          );
                                        }}
                                            >
                                              <Stack direction="row" alignItems="center" spacing={2}>
                                                <Avatar
                                                  sx={{
                                                    width: 32,
                                                    height: 32,
                                                    background: isSelected 
                                                      ? 'linear-gradient(135deg, #00a1dd 0%, #42a5f5 100%)'
                                                      : 'rgba(255, 255, 255, 0.1)',
                                                    transition: 'all 0.3s ease'
                                                  }}
                                                >
                                                  <Iconify 
                                                    icon={option.icon} 
                                                    width={16} 
                                                    sx={{ color: isSelected ? 'white' : 'text.secondary' }}
                                                  />
                                                </Avatar>
                                                <Box sx={{ flex: 1 }}>
                                                  <Typography 
                                                    variant="body2" 
                                                    sx={{ 
                                                      color: isSelected ? 'white' : 'text.secondary',
                                                      fontWeight: isSelected ? 600 : 400
                                                    }}
                                                  >
                                                    {option.label}
                                                  </Typography>
                                                  <Typography 
                                                    variant="caption" 
                                                    sx={{ 
                                                      color: isSelected ? 'rgba(255, 255, 255, 0.7)' : 'text.disabled'
                                                    }}
                                                  >
                                                    Contest Type
                                                  </Typography>
                                                </Box>
                                                <Checkbox
                                                  checked={isSelected}
                                                  sx={{
                                                    color: 'primary.main',
                                                    '&.Mui-checked': {
                                                      color: 'primary.main',
                                                    },
                                                  }}
                                                />
                                              </Stack>
                                            </Paper>
                              </Grid>
                                        );
                                      })}
                          </Grid>
                                    
                          {touched.allowedGameTypes?.[idx]?.allowedContestTypes &&
                            errors.allowedGameTypes?.[idx]?.allowedContestTypes && (
                                        <Alert severity="error" sx={{ mt: 3 }}>
                                {errors.allowedGameTypes[idx].allowedContestTypes}
                                        </Alert>
                            )}
                        </AccordionDetails>
                      </Accordion>
                              ))}
                            </Stack>
                          ) : (
                            <Box
                              sx={{
                                p: 6,
                                textAlign: 'center',
                                borderRadius: 3,
                                background: 'linear-gradient(145deg, rgba(255, 255, 255, 0.02) 0%, rgba(255, 255, 255, 0.05) 100%)',
                                border: '2px dashed',
                                borderColor: 'divider'
                              }}
                            >
                              <Avatar
                                sx={{
                                  width: 80,
                                  height: 80,
                                  mx: 'auto',
                                  mb: 3,
                                  background: 'linear-gradient(135deg, #9c27b0 0%, #e91e63 100%)',
                                  boxShadow: '0 8px 32px rgba(156, 39, 176, 0.3)'
                                }}
                              >
                                <Iconify icon="mdi:gamepad-variant" width={40} />
                              </Avatar>
                              <Typography variant="h6" color="text.secondary" sx={{ mb: 2, fontWeight: 600 }}>
                                No Game Types Configured
                              </Typography>
                              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                                Game types will be configured automatically based on system settings
                    </Typography>
                              <Chip
                                label="Auto-Configuration Enabled"
                                sx={{
                                  backgroundColor: 'rgba(156, 39, 176, 0.2)',
                                  color: '#e91e63',
                                  fontWeight: 600,
                                  border: '1px solid rgba(156, 39, 176, 0.3)'
                                }}
                              />
                            </Box>
                  )}
                </>
              )}
            />
                  </CardContent>
                </Card>

            {/* Form Actions */}
                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    borderRadius: 3,
                    background: 'linear-gradient(135deg, #1a202c 0%, #2d3748 100%)',
                    border: '1px solid',
                    borderColor: 'divider',
                    position: 'sticky',
                    bottom: 16,
                    zIndex: 99
                  }}
                >
            <Stack
                    direction={{ xs: 'column', sm: 'row' }}
              spacing={2}
                    justifyContent="space-between"
                    alignItems={{ xs: 'stretch', sm: 'center' }}
                  >
                    <Button
                      size="large"
                      variant="outlined"
                      startIcon={<ArrowBackIcon />}
                      onClick={() => navigate("/boosters-management")}
                      sx={{
                        borderRadius: 2,
                        borderColor: 'divider',
                        color: 'text.secondary',
                        '&:hover': {
                          borderColor: 'primary.main',
                          color: 'primary.main',
                        }
                      }}
                    >
                      Back to List
                    </Button>
                    
                    <Stack direction="row" spacing={2}>
              <Button
                size="large"
                type="submit"
                variant="contained"
                disabled={isSubmitting}
                        startIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : <Iconify icon="mdi:content-save" />}
                        sx={{
                          borderRadius: 2,
                          background: 'linear-gradient(135deg, #00a1dd 0%, #42a5f5 100%)',
                          boxShadow: '0 8px 32px rgba(0, 161, 221, 0.3)',
                          '&:hover': {
                            background: 'linear-gradient(135deg, #0091c8 0%, #3b8fd9 100%)',
                            boxShadow: '0 12px 40px rgba(0, 161, 221, 0.4)',
                            transform: 'translateY(-2px)',
                          },
                          '&:disabled': {
                            background: 'rgba(255, 255, 255, 0.1)',
                            color: 'text.disabled',
                          }
                        }}
                      >
                        {isSubmitting ? "Saving..." : (id ? "Update Booster" : "Create Booster")}
              </Button>
                    </Stack>
                  </Stack>
                </Paper>
            </Stack>
          </Form>
        </FormikProvider>
        </Stack>
      </Fade>
    </Container>
  );
}
